import { useState, useEffect, useCallback } from "react";
import { TextField, Button } from "@mui/material";
import { Formik } from "formik";
import DataTable from "../../components/Table/DataTable";
import {
  getAllServerUrls,
  addServerUrl,
  updateServerUrl,
} from "../../services/serverUrls/serverUrl.api";
import { toast } from "../../components/Toast/Toast";
import { Tooltip, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { serverUrlSchema } from "../../schemas/serverUrlSchema";

export default function ServerURLs() {
  const [serverUrls, setServerUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchText, setSearchText] = useState("");
  const [selectedUrl, setSelectedUrl] = useState(null);

  const fetchServerUrls = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllServerUrls({
        ...(searchText ? { search: searchText } : {}),
        page: page + 1,
        limit: rowsPerPage,
      });
      setServerUrls(response.data);
      setTotalPage(response.totalResults);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to fetch server URLs");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchText]);

  useEffect(() => {
    const delay = searchText ? 500 : 0;
    const debounceTimer = setTimeout(fetchServerUrls, delay);
    return () => clearTimeout(debounceTimer);
  }, [fetchServerUrls, rowsPerPage, searchText]);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true);
      if (selectedUrl?._id) {
        await updateServerUrl(selectedUrl._id, values);
        toast.success("Server URL updated successfully");
      } else {
        await addServerUrl(values);
        toast.success("Server URL added successfully");
      }
      resetForm();
      setSelectedUrl(null);
      fetchServerUrls();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save server URL");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 50,
      renderCell: (row) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => setSelectedUrl(row)}
            className="text-primary hover:bg-primary/10"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 400,
    },
    {
      field: "value",
      headerName: "Server URL",
    },
  ];

  return (
    <div className="p-4">
      <div className="flex flex-row gap-2 w-full max-w-4xl mb-4">
        <TextField
          label="Search URL"
          value={searchText}
          size="small"
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          sx={{ minWidth: "200px" }}
          InputLabelProps={{ shrink: true }}
        />
        <Formik
          initialValues={{
            name: selectedUrl?.name || "",
            value: selectedUrl?.value || "",
          }}
          enableReinitialize
          validationSchema={serverUrlSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit: formikSubmit,
          }) => (
            <form
              onSubmit={formikSubmit}
              className="flex flex-row gap-2 w-full"
            >
              <TextField
                fullWidth
                label="URL Name"
                name="name"
                value={values.name}
                size="small"
                onChange={handleChange}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="URL Value"
                name="value"
                value={values.value}
                size="small"
                onChange={handleChange}
                error={touched.value && Boolean(errors.value)}
                helperText={touched.value && errors.value}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!values.name?.trim() || !values.value?.trim()}
                sx={{ textTransform: "none", fontWeight: "bold" }}
              >
                {selectedUrl ? "Update" : "Add"}
              </Button>
            </form>
          )}
        </Formik>
      </div>
      <DataTable
        columns={columns}
        rows={serverUrls}
        loading={loading}
        totalCount={totalPage}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </div>
  );
}
